import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Trophy, Crown, ExternalLink, Sparkles, Info, MapPin, Search, X } from 'lucide-react';
import { CATEGORIES_2021 } from '../data/winners2021';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wikimedia commons helper (stable, high-quality, free-use)
const wm = (filename) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(filename)}?width=1200`;

// 5 TWBL categories in display order
const TWBL_CATEGORY_IDS = [
  'luxury-destination',
  'luxury-accommodation',
  'luxury-air',
  'luxury-experience',
  'luxury-b2b',
];

// Curated stock image fallbacks for finalists not in the awards DB
const FALLBACK_IMAGE_BY_CATEGORY = {
  'luxury-air': 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?w=1200&q=80&auto=format&fit=crop',
  'luxury-b2b': 'https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=1200&q=80&auto=format&fit=crop',
  'luxury-destination': 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&q=80&auto=format&fit=crop',
  'luxury-accommodation': 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&q=80&auto=format&fit=crop',
  'luxury-experience': 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1200&q=80&auto=format&fit=crop',
};

// Hand-tuned per-winner image overrides (priority over DB image_url).
// Keyed by lowercase winner.name.
const WINNER_IMAGE_OVERRIDES = {
  // VistaJet — official Bombardier Global jet in VistaJet livery (Wikimedia)
  'vistajet': wm('Vista Jet Bombardier BD-700-1A10 Global Express XRS; OE-LXR@ZRH;08.06.2011 (5832393313) (cropped).jpg'),
  // ALHI — official footer logo (full wordmark) from alhi.com/about-us
  'alhi (associated luxury hotels international)': 'https://www.alhi.com/includes/client_public/assets/shared/logos/footer-logo.svg',
};

// Hand-tuned per-finalist image overrides (priority over DB image_url).
// Keyed by lowercase finalist name (or its primary segment before the comma).
const FINALIST_IMAGE_OVERRIDES = {
  // Luxury Accommodation
  'raffles hotel, singapore': wm('Raffles Hotel Singapore.JPG'),
  'viceroy resort, bali, indonesia': 'https://images.unsplash.com/photo-1602002418082-a4443e081dd1?w=1200&q=80&auto=format&fit=crop',
  // Luxury Air — private jet operators
  'jetex': wm('Bombardier Global 7500, Paris Air Show 2019, Le Bourget (SIAE8909).jpg'),
  'jetfly': wm('LX-JFU Pilatus PC-12 Jetfly Aviation (34901057414).jpg'),
  'jsx': wm('JSX ERJ at Crucial MRO at KMDH.jpg'),
  'diamonte jets': 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?w=1200&q=80&auto=format&fit=crop',
  // Luxury B2B — official logos (rendered via LOGO_BRANDS handling)
  'ahla, united states': 'https://www.ahla.com/themes/custom/ahla_v2/assets/img/ahla-logo.png',
  'compass edge, hong kong': 'https://www.compass-edge.com/wp-content/uploads/2019/07/cropped-cropped-CompassEdge-min.png',
  // Travel-Awards.org → handled as a text logo (no image URL available)
};

// Names (lowercase) that should render as a "logo card" — logo on dark bg, object-contain.
const LOGO_BRANDS = new Set([
  'alhi (associated luxury hotels international)',
  'ahla, united states',
  'compass edge, hong kong',
  'travel-awards.org',
]);

// Names (lowercase) that have no image and render as a styled text logo.
const TEXT_LOGO_BRANDS = new Set([
  'travel-awards.org',
]);

// Per-finalist clickable URLs (winners already carry their own .url).
const FINALIST_URL_OVERRIDES = {
  'ahla, united states': 'https://www.ahla.com/',
  'compass edge, hong kong': 'https://www.compass-edge.com/',
  'travel-awards.org': 'https://travel-awards.org/',
};

const TWBL_CATEGORIES = TWBL_CATEGORY_IDS
  .map((id) => CATEGORIES_2021.find((c) => c.id === id))
  .filter(Boolean)
  .map((c) => ({
    ...c,
    displayTitle: c.title.replace(/^World's Best Luxury\s+/i, ''),
  }));

/**
 * Try to find an award in the DB by fuzzy name match.
 * Strips trailing location suffixes like ", Singapore" / ", Dubai, UAE".
 */
function findAward(awardsByName, rawName) {
  if (!rawName) return null;
  const base = rawName.toLowerCase().trim();
  if (awardsByName[base]) return awardsByName[base];
  // Try without comma-suffixed location: "Burj Al Arab Jumeirah, Dubai, UAE" → "burj al arab jumeirah"
  const noLocation = base.split(',')[0].trim();
  if (awardsByName[noLocation]) return awardsByName[noLocation];
  // Try common alias mappings
  const aliasMap = {
    'maldives': 'maldives tourism',
    'singapore': 'singapore tourism',
    'australia': 'australia tourism',
    'new zealand': 'new zealand tourism',
    'canada': 'canada tourism',
    'seychelles': 'seychelles tourism',
    'qatar': 'qatar tourism',
    'dubai, uae': 'dubai tourism',
  };
  const alias = aliasMap[noLocation];
  if (alias && awardsByName[alias]) return awardsByName[alias];
  return null;
}

export default function TwblAwardsPage() {
  const [awardsByName, setAwardsByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    axios
      .get(`${API_URL}/api/awards`)
      .then((res) => {
        const m = {};
        for (const a of res.data || []) {
          if (a?.name) m[a.name.toLowerCase()] = a;
        }
        setAwardsByName(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Apply category + search filters to the visible sections
  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TWBL_CATEGORIES
      .filter((cat) => activeCategory === 'all' || cat.id === activeCategory)
      .map((cat) => {
        if (!q) return cat;
        const winnerMatches = cat.winner.name.toLowerCase().includes(q);
        const matchingFinalists = cat.finalists.filter((f) =>
          f.toLowerCase().includes(q)
        );
        if (!winnerMatches && matchingFinalists.length === 0) return null;
        return {
          ...cat,
          finalists: winnerMatches ? cat.finalists : matchingFinalists,
          _winnerMatched: winnerMatches,
        };
      })
      .filter(Boolean);
  }, [query, activeCategory]);

  const filters = [
    { id: 'all', label: 'All Categories' },
    ...TWBL_CATEGORIES.map((c) => ({ id: c.id, label: c.displayTitle })),
  ];

  return (
    <main className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="twbl-awards-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <section className="text-center mb-14">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4 flex items-center justify-center gap-2">
            <Crown className="w-4 h-4" />
            TWBL Awards
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
            The World's Best
            <br />
            <span className="text-gradient-gold">Luxury Awards</span>
          </h1>
          <a
            href="https://www.theworldsbestluxury.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E8C76A] transition-colors font-['Outfit'] text-sm sm:text-base tracking-wide"
            data-testid="twbl-external-link"
          >
            theworldsbestluxury.com
            <ExternalLink className="w-4 h-4" />
          </a>
        </section>

        {/* Intro */}
        <div className="text-center mb-8">
          <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-medium text-white">
            TWBL Awards Winners
          </h2>
        </div>

        {/* Search + Category filter */}
        <div className="mb-12 space-y-5" data-testid="twbl-search-filter">
          {/* Search input */}
          <div className="relative max-w-xl mx-auto">
            <Search className="w-4 h-4 text-[#D4AF37]/70 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search winners and finalists…"
              className="w-full bg-[#121212] border border-white/10 focus:border-[#D4AF37]/50 rounded-full pl-11 pr-11 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/30 transition-all font-['Outfit']"
              data-testid="twbl-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                aria-label="Clear search"
                data-testid="twbl-search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {filters.map((f) => {
              const isActive = activeCategory === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setActiveCategory(f.id)}
                  data-testid={`twbl-cat-${f.id}`}
                  className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm uppercase tracking-[0.15em] font-medium transition-all border ${
                    isActive
                      ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.25)]'
                      : 'bg-transparent text-white/60 border-white/10 hover:text-white hover:border-[#D4AF37]/40'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="space-y-12">
            {[...Array(3)].map((_, i) => <div key={i} className="h-96 skeleton rounded-2xl" />)}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div
            className="text-center py-24 border border-dashed border-white/10 rounded-2xl"
            data-testid="twbl-no-results"
          >
            <Search className="w-8 h-8 text-[#D4AF37]/50 mx-auto mb-4" />
            <p className="font-['Playfair_Display'] text-2xl text-white mb-2">No results found</p>
            <p className="text-white/50 text-sm">
              Try a different search term or category.
            </p>
          </div>
        ) : (
          <div className="space-y-20">
            {filteredCategories.map((cat) => (
              <TwblCategorySection
                key={cat.id}
                category={cat}
                awardsByName={awardsByName}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function TwblCategorySection({ category, awardsByName }) {
  // Resolve winner image (override > DB > fallback)
  const winnerKey = (category.winner.name || '').toLowerCase().trim();
  const winnerAward = findAward(awardsByName, category.winner.name);
  const winnerOverride = WINNER_IMAGE_OVERRIDES[winnerKey];
  const winnerImage = winnerOverride || winnerAward?.image_url || FALLBACK_IMAGE_BY_CATEGORY[category.id];
  const winnerIsLogo = LOGO_BRANDS.has(winnerKey);

  return (
    <section data-testid={`twbl-section-${category.id}`}>
      {/* Section header */}
      <div className="flex items-center gap-3 mb-8">
        <Trophy className="w-5 h-5 text-[#D4AF37]" />
        <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">
          World's Best Luxury {category.displayTitle}
        </p>
        <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
      </div>

      {/* Winner — hero card */}
      <a
        href={category.winner.url || '#'}
        target={category.winner.url ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="group block relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#121212] mb-10"
        data-testid={`twbl-winner-${category.id}`}
      >
        <div className="relative h-72 sm:h-96 overflow-hidden">
          {winnerIsLogo ? (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#121212] via-[#0F0F0F] to-[#0A0A0A] p-10">
              <div className="w-full max-w-2xl h-full max-h-64 rounded-2xl bg-[#F8F4EC] flex items-center justify-center p-12 shadow-[0_0_60px_rgba(212,175,55,0.22)] ring-1 ring-[#D4AF37]/30">
                <img
                  src={winnerImage}
                  alt={category.winner.name}
                  className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
          ) : (
            <img
              src={winnerImage}
              alt={category.winner.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          )}
          {!winnerIsLogo && (
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
          )}

          <div className="absolute top-5 left-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider shadow-lg">
              <Trophy className="w-3.5 h-3.5" />
              Winner
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent">
            <p className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37]/90 mb-2">
              World's Best Luxury {category.displayTitle}
            </p>
            <h3 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-2">
              {category.winner.name}
            </h3>
            {category.winner.subtitle && (
              <p className="text-white/70 text-sm sm:text-base mb-3">{category.winner.subtitle}</p>
            )}
            {category.winner.url && (
              <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold group-hover:gap-3 transition-all">
                Visit Site
                <ExternalLink className="w-4 h-4" />
              </span>
            )}
          </div>
        </div>
      </a>

      {/* Finalists */}
      <p className="text-white/50 text-xs uppercase tracking-[0.25em] mb-5">Finalists</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {category.finalists.map((finalistName, i) => (
          <FinalistCard
            key={i}
            name={finalistName}
            categoryId={category.id}
            awardsByName={awardsByName}
          />
        ))}
      </div>

      {/* Optional category note */}
      {category.note && (
        <div className="mt-8 p-4 rounded-lg bg-[#0A0A0A] border border-[#D4AF37]/20 flex gap-3">
          <Info className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
          <p className="text-white/70 text-xs leading-relaxed">{category.note}</p>
        </div>
      )}
    </section>
  );
}

function FinalistCard({ name, categoryId, awardsByName }) {
  const key = name.toLowerCase().trim();
  const award = findAward(awardsByName, name);
  const override = FINALIST_IMAGE_OVERRIDES[key];
  // Override takes priority so explicitly chosen brand assets win over generic DB images
  const image = override || award?.image_url || FALLBACK_IMAGE_BY_CATEGORY[categoryId];
  const isLogo = LOGO_BRANDS.has(key);
  const isTextLogo = TEXT_LOGO_BRANDS.has(key);
  const url = FINALIST_URL_OVERRIDES[key];

  // For names with "X, Country" → display name on first line, location on second
  const [primary, ...rest] = name.split(',').map((s) => s.trim());
  const location = rest.join(', ');

  const Wrapper = url ? 'a' : 'article';
  const wrapperProps = url
    ? { href: url, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className="group block rounded-xl overflow-hidden border border-white/5 bg-[#121212] hover:border-[#D4AF37]/40 transition-all"
      data-testid={`twbl-finalist-${primary.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="relative h-44 overflow-hidden">
        {isTextLogo ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#161616] via-[#101010] to-[#0A0A0A] p-6">
            <div className="font-['Playfair_Display'] text-3xl text-white tracking-tight transition-transform duration-500 group-hover:scale-105">
              Travel-Awards
            </div>
            <div className="font-['Outfit'] text-[#D4AF37] text-xs tracking-[0.4em] mt-1 uppercase">
              .ORG
            </div>
          </div>
        ) : isLogo ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#161616] via-[#101010] to-[#0A0A0A] p-5">
            <div className="w-full h-full rounded-lg bg-[#F8F4EC] flex items-center justify-center p-5 shadow-[0_0_24px_rgba(212,175,55,0.18)] ring-1 ring-[#D4AF37]/30">
              <img
                src={image}
                alt={primary}
                className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </div>
        ) : (
          <>
            <img
              src={image}
              alt={primary}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
          </>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-['Playfair_Display'] text-lg font-semibold text-white group-hover:text-[#D4AF37] transition-colors line-clamp-2">
            {primary}
          </h4>
          {url && (
            <ExternalLink className="w-4 h-4 text-[#D4AF37]/70 group-hover:text-[#D4AF37] shrink-0 mt-1.5" />
          )}
        </div>
        {location && (
          <p className="text-white/50 text-xs mt-1 flex items-center gap-1.5">
            <MapPin className="w-3 h-3" />
            {location}
          </p>
        )}
      </div>
    </Wrapper>
  );
}
